import React, { useState } from "react";
import { useFetch } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import { PageHeader, Panel, StatusChip, LoadingState, CopyButton } from "@/components/app/primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const DIRECTIONS = [
  { key: "upward", label: "Upward", desc: "Sponsor updates, decision requests, no-surprises escalations" },
  { key: "outward", label: "Outward", desc: "Cross-functional owner asks, dependency requests, session outputs" },
  { key: "downward", label: "Downward", desc: "Huddle cards, save prompts, action cards for frontline teams" },
  { key: "customer", label: "Customer-facing", desc: "Status updates, expectation setting, save outreach language" },
];

const ACID_LABELS = {
  action_clear: "Action clear", outcome_clear: "Outcome clear", audience_clear: "Audience clear", owner_clear: "Owner clear",
  timing_clear: "Timing clear", complete: "Complete", concise: "Concise", current: "Current", logical: "Logically structured",
  no_overclaim: "No overclaiming", facts_separated: "Facts vs hypotheses separated",
};

export default function CommsStudioPage() {
  const { data: comms, loading } = useFetch("/communications");
  const [expanded, setExpanded] = useState(null);

  if (loading) return <LoadingState message="Loading communication artifacts..." />;

  return (
    <div data-testid="comms-page">
      <PageHeader
        kicker="Communications as a delivery control"
        title="Communications Studio"
        subtitle="Every artifact passes the SIX Communications Acid Test before it ships: action, outcome, audience, owner, timing - clear, complete, concise, current, logical, honest."
      />

      <Tabs defaultValue="upward" data-testid="comms-direction-tabs">
        <TabsList className="bg-[--shell-2] border border-[--hairline] mb-1 flex-wrap h-auto">
          {DIRECTIONS.map((d) => (
            <TabsTrigger key={d.key} value={d.key} data-testid={`comms-tab-${d.key}`}
              className="text-xs data-[state=active]:bg-[--six-crimson-soft] data-[state=active]:text-[--tx-1]">
              {d.label} ({(comms || []).filter((c) => c.direction === d.key).length})
            </TabsTrigger>
          ))}
        </TabsList>

        {DIRECTIONS.map((d) => (
          <TabsContent key={d.key} value={d.key}>
            <p className="text-xs text-[--tx-3] mb-3">{d.desc}</p>
            <div className="grid lg:grid-cols-2 gap-3">
              {(comms || []).filter((c) => c.direction === d.key).map((c) => {
                const passed = Object.values(c.acid_test).filter(Boolean).length;
                const isOpen = expanded === c.id;
                return (
                  <div key={c.id} className="rounded-xl border border-[--hairline] bg-[--panel-dark] p-3.5" data-testid={`comm-card-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="font-mono text-[10px] text-[--tx-3]">{c.id}</span>
                      <StatusChip tone="neutral">{c.audience}</StatusChip>
                      <StatusChip tone={passed === 11 ? "validated" : "risk"} className="ml-auto" data-testid={`acid-score-${c.id}`}>Acid test {passed}/11</StatusChip>
                    </div>
                    <h4 className="text-[13px] font-semibold text-[--tx-1] mb-2">{c.title}</h4>
                    <pre className={`text-xs text-[--tx-2] leading-relaxed whitespace-pre-wrap font-sans rounded-lg bg-[--shell-2] border border-[--divider] p-3 ${isOpen ? "" : "max-h-36 overflow-hidden"}`}>{c.body}</pre>
                    <div className="flex items-center gap-2 mt-2.5">
                      <button onClick={() => setExpanded(isOpen ? null : c.id)} data-testid={`comm-expand-${c.id}`}
                        className="text-[11px] font-medium text-[--ai] hover:text-[#35B7B1] transition-colors">
                        {isOpen ? "Collapse" : "Read full + acid test"}
                      </button>
                      <div className="ml-auto"><CopyButton text={c.body} label="Copy" testId={`comm-copy-${c.id}`} /></div>
                    </div>
                    {isOpen && (
                      <div className="mt-3 pt-3 border-t border-[--divider]">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-2">Acid test detail</div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {Object.entries(ACID_LABELS).map(([k, label]) => (
                            <div key={k} className="flex items-center gap-1.5 text-[11px]">
                              {c.acid_test[k] ? <CheckCircle2 size={12} className="text-[--green] shrink-0" /> : <XCircle size={12} className="text-[--danger] shrink-0" />}
                              <span className={c.acid_test[k] ? "text-[--tx-2]" : "text-[--danger]"}>{label}</span>
                            </div>
                          ))}
                        </div>
                        {c.note && <p className="text-[11px] text-[--amber] mt-2 leading-snug">{c.note}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Panel title="The Communications Acid Test" className="mt-4" testId="acid-test-reference">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
          {Object.values(ACID_LABELS).map((l) => (
            <div key={l} className="rounded-lg bg-[--shell-2] border border-[--divider] px-2.5 py-2 text-[11px] text-[--tx-2] text-center">{l}?</div>
          ))}
        </div>
        <p className="text-[11px] text-[--tx-3] mt-3 leading-relaxed">Action-based. Outcome-based. Audience-appropriate. Communication at SIX is a delivery control, not a formality - a message that fails the test does not ship.</p>
      </Panel>
    </div>
  );
}
